import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import InputLabel from "@/atoms-react/input/Input.react.tsx";
import { PasswordInput } from "@/atoms-react/input/password-input.react";
import { PasswordRequirements } from "@/atoms-react/input/password-requirements.react.tsx";
import { AuthApiClient } from "@/features/auth/api-client/api-client";
import { Routes } from "@/features/router";
import { ChangePasswordErrorCode } from "@/features/security/types";
import useCaptchaVerify from "@/features/utils/hooks/use_captcha_verify";
import { getRoute } from "@/utils/routes";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { FlowHeader } from "../../../flow-header.react";
import { usei18n } from "@/features/utils/hooks/use-i18n";

export const ResetPassword = () => {
  const { t } = useTranslation(["pass"]);
  const token = new URLSearchParams(document.location.search).get("token") || "";
  const id = new URLSearchParams(document.location.search).get("id") || "";

  const { session_uuid } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const { handleReCaptchaVerify } = useCaptchaVerify();
  const { getLanguage } = usei18n();

  const resetPassword = async () => {
    setLoading(true);
    let data;

    const captchaToken = await handleReCaptchaVerify();
    if (captchaToken) {
      data = await AuthApiClient.resetPassword({
        password,
        confirm_password: confirmPassword,
        id,
        token,
        language: getLanguage(),
        captcha_token: captchaToken || ""
      });
      if (!data?.success) {
        setLoading(false);
        toast.error(
          data?.errorCode === ChangePasswordErrorCode.WrongOTP
            ? t("olkypass.securityPage.passwordSection.2fa.modal.notifications.otp.error")
            : data?.errorCode === ChangePasswordErrorCode.AlreadyUsed
            ? t("flow.id_control.error.password_already_used")
            : t("flow.id_control.error.password_not_correct")
        );
      } else {
        toast.success(t("flow.id_control.label.reset_success"));
        await new Promise((resolve) => setTimeout(resolve, 2000));

        navigate(getRoute(Routes.flow_block, { session_uuid, block: "welcome" }), {
          replace: true
        });
      }
      setLoading(false);
    }
  };

  return (
    <>
      <Column>
        <div className="mb-12 flex grow flex-col">
          <FlowHeader
            back={false}
            title={t("flow.id_control.label.reset_title")}
            description={t("flow.id_control.label.reset_description")}
          />
          <PasswordInput
            onChange={({ password }) => {
              setPassword(password);
            }}
            label={t("olkypass.securityPage.passwordSection.input.new_pwd")}
          />
          <InputLabel
            type="password"
            placeholder={t("flow.id_control.input.placeholder_password")}
            value={confirmPassword}
            onChange={(value: string) => setConfirmPassword(value)}
            label={t("olkypass.securityPage.passwordSection.input.confirm_new_pwd")}
            className={"mb-1"}
          />
          <PasswordRequirements
            password={password}
            confirmPassword={confirmPassword}
            setValidity={({ valid }) => setIsPasswordValid(valid)}
          />
        </div>

        <div className="shrink-0">
          <Button
            shortcut="enter"
            onClick={() => resetPassword()}
            disabled={!isPasswordValid || !confirmPassword || confirmPassword !== password}
            loading={loading}
          >
            {t("flow.id_control.input.save")}
          </Button>
        </div>
      </Column>
    </>
  );
};
